import React, { useState } from 'react'
import { MessageCircle, KeyRound, LifeBuoy, UserPlus, CreditCard, Shield } from 'lucide-react'
import { FilterChip, NotAvailable } from '../ui'
import { TONE, formatDateTime } from '../../lib/support'

const KIND_STYLE = {
  whatsapp: [MessageCircle, 'warn'],
  access: [KeyRound, 'muted'],
  support: [LifeBuoy, 'ok'],
  clients: [UserPlus, 'ok'],
  billing: [CreditCard, 'muted'],
  admin: [Shield, 'muted'],
}

const FILTERS = [
  ['all', 'Tudo'],
  ['whatsapp', 'WhatsApp'],
  ['access', 'Acesso'],
  ['billing', 'Cobrança'],
  ['clients', 'Clientes'],
  ['support', 'Suporte'],
]

const KindIcon = ({ kind }) => {
  const [Icon, tone] = KIND_STYLE[kind] || KIND_STYLE.admin
  return (
    <span className={`grid h-7 w-7 flex-none place-items-center rounded-full ${TONE[tone].tag}`}>
      <Icon className="h-[13px] w-[13px]" />
    </span>
  )
}

export const ActivityItem = ({ event, compact = false }) =>
  compact ? (
    <div className="grid grid-cols-[28px_minmax(0,1fr)] items-start gap-2.5">
      <KindIcon kind={event.kind} />
      <div className="flex flex-col leading-snug">
        <span className="text-[13px]">{event.text}</span>
        <span className="text-[11px] text-admin-500">
          {formatDateTime(event.when)}
          {event.actor ? ` · ${event.actor}` : ''}
        </span>
      </div>
    </div>
  ) : (
    <div className="grid grid-cols-[110px_28px_minmax(0,1fr)] items-start gap-3">
      <span className="pt-1.5 text-xs tabular-nums text-admin-500">{formatDateTime(event.when)}</span>
      <KindIcon kind={event.kind} />
      <div className="flex flex-col pt-0.5 leading-snug">
        <span className="text-[13px]">{event.text}</span>
        {event.actor && <span className="text-[11px] text-admin-500">{event.actor}</span>}
      </div>
    </div>
  )

/**
 * Until GET /tenants/:id/activity exists, the log is assembled from what the API already
 * exposes (staff sign-ins, support chats, tenant dates) — `activity.derived` says so.
 */
export const buildDerivedActivity = (tenant, staff = [], chats = []) => {
  const events = []
  staff.forEach((u) => {
    if (u.last_login_at) events.push({ when: u.last_login_at, kind: 'access', text: `${u.name || u.email} entrou`, actor: 'Portal do estúdio' })
  })
  chats.forEach((c) => {
    const first = c.messages?.find((m) => m.role === 'user')?.content
    events.push({
      when: c.created_at,
      kind: 'support',
      text: first ? `Conversa de suporte iniciada: “${first.length > 80 ? `${first.slice(0, 80)}…` : first}”` : 'Conversa de suporte iniciada',
      actor: c.user_name || c.user_email || 'Usuário do estúdio',
    })
  })
  if (tenant?.deactivated_at) {
    events.push({
      when: tenant.deactivated_at,
      kind: 'admin',
      text: `Estúdio suspenso${tenant.deactivation_reason ? ` · ${tenant.deactivation_reason}` : ''}`,
      actor: 'Plataforma',
    })
  }
  if (tenant?.created_at) events.push({ when: tenant.created_at, kind: 'admin', text: 'Estúdio criado', actor: 'Plataforma' })
  return events.sort((a, b) => new Date(b.when) - new Date(a.when))
}

export const ActivityTab = ({ activity }) => {
  const [filter, setFilter] = useState('all')
  const rows = activity.events.filter((e) => filter === 'all' || e.kind === filter)
  return (
    <section className="card flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="flex-1 text-sm">Registro de atividades</h4>
        {FILTERS.map(([key, label]) => (
          <FilterChip key={key} size="sm" selected={filter === key} onClick={() => setFilter(key)}>
            {label}
          </FilterChip>
        ))}
      </div>
      {activity.derived && (
        <NotAvailable>
          Montado a partir de acessos, conversas de suporte e datas do estúdio. Mudanças de estado do WhatsApp, pagamentos,
          importações de CSV e ações de admin aparecerão quando o endpoint de atividades for implantado.
        </NotAvailable>
      )}
      {rows.length === 0 ? (
        <p className="text-[13px] text-admin-600">Nada nesta categoria.</p>
      ) : (
        rows.map((e, i) => <ActivityItem key={i} event={e} />)
      )}
    </section>
  )
}
